import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, Download, Calendar, FileText, RefreshCw, Users,
  Check, Clock, X, DoorOpen, AlertTriangle, Sheet
} from 'lucide-react';
import { motion } from 'framer-motion';

const API = typeof window !== 'undefined' && window.location.hostname !== 'localhost' && !window.location.hostname.includes('preview.emergentagent.com') ? `${window.location.origin}/api` : process.env.REACT_APP_BACKEND_URL + '/api';
const safeFetch = (...args) => (window.__origFetch || window.fetch)(...args);

export default function AttendanceReports({ onClose }) {
  const [dates, setDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const res = await safeFetch(`${API}/summer2026/attendance/history`);
      const data = await res.json();
      if (data.success) setDates(data.dates);
    } catch (err) {
      console.error('Fetch history error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchHistory(); }, []);

  const handleDownloadPDF = async (date) => {
    setDownloading(`pdf_${date}`);
    try {
      const res = await safeFetch(`${API}/summer2026/attendance/report/pdf?date=${date}`);
      if (!res.ok) throw new Error('PDF generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${date}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download error:', err);
      alert('Failed to generate PDF. Make sure attendance has been recorded for this date.');
    } finally {
      setDownloading(null);
    }
  };

  const handleDownloadExcel = async (date) => {
    setDownloading(`excel_${date}`);
    try {
      const res = await safeFetch(`${API}/summer2026/attendance/report/excel?date=${date}`);
      if (!res.ok) throw new Error('Excel generation failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel download error:', err);
      alert('Failed to generate Excel file.');
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr) => {
    try {
      const d = new Date(dateStr + 'T12:00:00');
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#F1F5F9] pb-24">
      <div className="bg-[#0055A4] py-4 px-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center">
            <button onClick={onClose} className="text-white mr-3" data-testid="reports-close-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-white text-xl font-bold font-['Fredoka']">Attendance Reports</h1>
              <p className="text-white/70 text-xs mt-0.5">Download PDF or Excel reports for any day</p>
            </div>
          </div>
          <Button
            onClick={fetchHistory}
            variant="ghost"
            className="text-white hover:bg-white/20 rounded-xl"
            data-testid="reports-refresh-btn"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {loading && (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-8 h-8 text-[#0055A4] animate-spin" />
            <span className="ml-3 text-[#475569]">Loading history...</span>
          </div>
        )}

        {!loading && dates.length === 0 && (
          <div className="bg-white rounded-2xl border p-8 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#475569] font-medium">No attendance records yet</p>
            <p className="text-xs text-[#94A3B8] mt-1">Records appear here after attendance is taken in group views</p>
          </div>
        )}

        {!loading && dates.length > 0 && (
          <div className="space-y-3">
            {dates.map((d, idx) => (
              <motion.div
                key={d.date}
                className="bg-white rounded-2xl shadow-sm border border-[#E2E8F0] p-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                      <FileText className="w-6 h-6 text-[#0055A4]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-[#0F172A] font-['Fredoka']">{formatDate(d.date)}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1 text-xs text-[#475569]">
                          <Users className="w-3 h-3" /> {d.total_expected || d.count} expected
                        </span>
                        <span className="inline-flex items-center gap-1 text-xs text-green-600">
                          <Check className="w-3 h-3" /> {d.present}
                        </span>
                        {d.late > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                            <Clock className="w-3 h-3" /> {d.late} late
                          </span>
                        )}
                        {d.absent > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600">
                            <X className="w-3 h-3" /> {d.absent} absent
                          </span>
                        )}
                        {d.leaving > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-blue-600">
                            <DoorOpen className="w-3 h-3" /> {d.leaving} leaving
                          </span>
                        )}
                        {d.not_selected > 0 && (
                          <span className="inline-flex items-center gap-1 text-xs text-yellow-700 font-semibold" data-testid={`not-selected-count-${d.date}`}>
                            <AlertTriangle className="w-3 h-3" /> {d.not_selected} not selected
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      onClick={() => handleDownloadPDF(d.date)}
                      disabled={downloading === `pdf_${d.date}`}
                      className="bg-[#0055A4] hover:bg-[#004080] text-white rounded-xl px-3"
                      data-testid={`download-pdf-${d.date}`}
                    >
                      {downloading === `pdf_${d.date}` ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Download className="w-4 h-4 mr-1" />
                      )}
                      PDF
                    </Button>
                    <Button
                      onClick={() => handleDownloadExcel(d.date)}
                      disabled={downloading === `excel_${d.date}`}
                      variant="outline"
                      className="border-green-600 text-green-700 hover:bg-green-50 rounded-xl px-3"
                      data-testid={`download-excel-${d.date}`}
                    >
                      {downloading === `excel_${d.date}` ? (
                        <RefreshCw className="w-4 h-4 animate-spin mr-1" />
                      ) : (
                        <Sheet className="w-4 h-4 mr-1" />
                      )}
                      Excel
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-[#E2E8F0] py-2 px-4 z-30">
        <div className="max-w-3xl mx-auto flex items-center justify-center">
        </div>
      </div>
    </div>
  );
}
